/**
 * Traducciones al español de los selectores de fecha y hora
 * -----------------------------------------------------------------------------
 * Solo datos. Quien los aplica es `pickers.js`, que construye las instancias de
 * MDB con estas opciones.
 *
 * Exportadas para poder extenderlas o reutilizarlas desde el proyecto que
 * consume el theme.
 */

export const datepickerES = {
  // **La fecha se escribe como la entiende el servidor.**
  //
  // MDB usa `dd/mm/yyyy` por defecto y las aplicaciones de la suite guardan y
  // pintan `Y-m-d`: el `value` que llega del servidor viene en ISO, así que un
  // campo con fecha ya puesta se abría en un formato y se guardaba en otro. Al
  // elegir una con el calendario, el servidor contestaba «no es una fecha» -que
  // era verdad, para él- y el formulario volvía sin ella.
  //
  // Se veía en la misma pantalla: «Fecha 2026-07-23» al lado de «Vence
  // 29/08/2026», los dos campos iguales y con dos formatos.
  format: 'yyyy-mm-dd',
  title: 'Seleccionar fecha',
  monthsFull: [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ],
  monthsShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
  weekdaysFull: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
  weekdaysShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
  weekdaysNarrow: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
  okBtnText: 'Aceptar',
  clearBtnText: 'Limpiar',
  cancelBtnText: 'Cancelar',
  okBtnLabel: 'Confirmar selección',
  clearBtnLabel: 'Limpiar selección',
  cancelBtnLabel: 'Cancelar selección',
  nextMonthLabel: 'Mes siguiente',
  prevMonthLabel: 'Mes anterior',
  nextYearLabel: 'Año siguiente',
  prevYearLabel: 'Año anterior',
  nextMultiYearLabel: 'Siguientes 24 años',
  prevMultiYearLabel: 'Anteriores 24 años',
  switchToMultiYearViewLabel: 'Cambiar a lista de años',
  switchToDayViewLabel: 'Cambiar a lista de días',
  // La semana empieza en lunes, como es habitual en español.
  startDay: 1,
};

export const timepickerES = {
  okLabel: 'Aceptar',
  cancelLabel: 'Cancelar',
  clearLabel: 'Limpiar',
  invalidLabel: 'Formato de hora inválido',
};

export const datetimepickerES = {
  invalidLabel: 'Formato de fecha u hora inválido',
};

// Acciones rápidas que añade el theme (no existen en MDB).
export const quickActionsES = {
  today: 'Hoy',
  now: 'Ahora',
};

// Selector de mes y año (componente propio del theme).
export const monthPickerES = {
  title: 'Seleccionar mes y año',
  monthsShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
  prevYear: 'Año anterior',
  nextYear: 'Año siguiente',
  clear: 'Limpiar',
  current: 'Mes actual',
};

// Notificaciones (componente propio del theme).
export const notifyES = {
  close: 'Cerrar aviso',
};

// Formularios (componente propio del theme).
export const formES = {
  step: 'Paso',
  back: 'Atrás',
  next: 'Continuar',
  save: 'Guardar',
  cancel: 'Cancelar',
  required: 'Este campo es obligatorio',
  email: 'Escribe un correo válido',
  number: 'Escribe un número válido',
  invalid: 'El valor no tiene el formato esperado',
  min: 'El valor mínimo es',
  max: 'El valor máximo es',
  minLength: 'Escribe al menos caracteres:',
  saving: 'Guardando…',
  cancelTitle: '¿Descartar el formulario?',
  cancelMessage: 'Se perderá lo que llevas escrito. Esta acción no se puede deshacer.',
  cancelConfirm: 'Descartar',
  cancelKeep: 'Seguir editando',
};

// Diálogos de confirmación y aviso (componente propio del theme).
export const dialogES = {
  confirm: 'Confirmar',
  cancel: 'Cancelar',
  accept: 'Entendido',
};

// Campo de número de tarjeta.
export const cardES = {
  invalid: 'El número de tarjeta no es válido',
  incomplete: 'Falta completar el número',
};

// Buscador de los desplegables. El theme lo activa en todos, así que estos
// textos se ven en cualquier formulario.
export const selectES = {
  searchPlaceholder: 'Buscar…',
  noResultText: 'Sin coincidencias',
  // Desplegables que consultan al servidor.
  loading: 'Cargando…',
  error: 'No se pudo cargar la lista',
  retry: 'Reintentar',
  minChars: 'Escribe al menos {n} letras para buscar',
};

// Buscador del menú lateral (componente propio del theme).
export const sidenavSearchES = {
  placeholder: 'Buscar en el menú…',
  empty: 'Sin coincidencias',
};

// Vista de datos: tabla o tarjetas (componente propio del theme).
export const dataViewES = {
  table: 'Tabla',
  cards: 'Tarjetas',
  board: 'Columnas',
  empty: 'No hay registros para mostrar.',
  emptyGroup: 'Sin registros',
  ungrouped: 'Sin clasificar',
  previous: 'Anterior',
  next: 'Siguiente',
  // {pagina}, {paginas} y {total} se sustituyen por los números de la página.
  summary: 'Mostrando página {pagina} de {paginas} ({total} resultados)',
  // Con un solo registro. «1 resultados» se lee como un descuido del programa.
  summaryOne: 'Mostrando 1 resultado',
  // {n} es el tamaño de página.
  perPage: '{n} resultados por página',
  perPageOption: '{n} resultados',
  search: 'Buscar en el listado…',
  noMatches: 'Sin coincidencias',
  noMatchesText: 'Prueba con otras palabras o borra el filtro.',
  clearSearch: 'Borrar búsqueda',
  loadError: 'No se pudo cargar el listado',
  loadErrorText: 'Falló la consulta al servidor. Puedes reintentarlo.',
  retry: 'Reintentar',
  // Tablero: cada columna pagina por su cuenta.
  boardRange: '{pagina} de {paginas}',
  perPageColumn: '{n} por columna',
  boardSummary: '{total} resultados',
  boardSummaryOne: '1 resultado',
  sortBy: 'Ordenar',
  sortAsc: 'ascendente',
  sortDesc: 'descendente',
};

// Campo de teléfono (componente propio del theme).
export const phoneES = {
  search: 'Buscar país',
  noResults: 'Sin resultados',
  selectCountry: 'Seleccionar código de país',
};

export default { datepickerES, timepickerES, datetimepickerES, quickActionsES };

// Campo de archivo.
export const fileES = {
  choose: 'Elegir archivo',
  remove: 'Quitar archivo',
  // {max} son los megabytes permitidos.
  tooBig: 'El archivo supera el límite de {max} MB',
};

// Selector de negocio (componente propio del theme).
export const workspaceES = {
  title: '¿En qué negocio vas a trabajar?',
  text: 'Tus permisos son distintos en cada uno. Puedes cambiar cuando quieras.',
  label: 'Negocio',
  submit: 'Entrar',
};

// Campana de notificaciones (componente propio del theme).
export const notificationBellES = {
  open: 'Ver notificaciones',
  title: 'Notificaciones',
  readAll: 'Marcar todas como leídas',
  seeAll: 'Ver todas',
  empty: 'No tienes notificaciones.',
  // Vista completa.
  all: 'Todas',
  unread: 'Sin leer',
  allRead: 'No te queda ninguna sin leer.',
  today: 'Hoy',
  yesterday: 'Ayer',
};
